import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import Typography from '@material-ui/core/Typography';
import AccountIcon from 'mdi-react/AccountIcon';
import PhoneIcon from 'mdi-react/PhoneIcon';
import EmailIcon from 'mdi-react/EmailIcon';
import MapMarkerIcon from 'mdi-react/MapMarkerIcon';
import type { WWWCompanyDocument } from '../../common/documents/WWWCompanyDocument';
import { Header } from '../Header';
import { Banner } from '../Banner';
import { PremiumIcon } from '../PremiumIcon';
import { useCompanyDictionary } from './CompanyDictionary';
import { CompanyEditDialogContent } from './CompanyEditDialog';
import { EditContactPerson } from './EditContactPerson';
import { Skeleton, StyledLabel } from './CompanyFieldAndHelpers';
import { Grid, Column, Item, Spacer } from './CompanyGrid';
import { EditLocation } from './EditLocation';
import { Color, mediaQuery } from '../Theme';

const Styled = {
  Row: styled.div`
    display: flex;
    flex-wrap: wrap;

    .MuiTypography-h5 {
      font-size: 1.5rem;
    }

    p {
      margin: 0.2rem 0;
      white-space: pre-line;
    }
  `,
  Address: styled.div`
    display: flex;
    margin-top: 1rem;
    width: 33.33%;

    > div:last-child {
      flex: 1;
      margin-left: 1rem;

      p {
        display: flex;
        align-items: center;

        &:first-of-type {
          margin-bottom: 0.4rem;
        }

        svg {
          margin-right: 0.6rem;
          width: 16px;
          height: 16px;
          opacity: 0.5;
        }
      }
    }

    ${mediaQuery.medium(css`
      width: 50%;
    `)}

    ${mediaQuery.small(css`
      width: 100%;
    `)}
  `,
  Contact: styled.div`
    display: flex;
    margin-top: 1rem;
    width: 33.33%;

    > div:last-child {
      flex: 1;
      margin-left: 1rem;

      p {
        display: flex;
        align-items: center;

        &:first-of-type {
          margin-bottom: 0.4rem;
        }

        svg {
          margin-right: 0.6rem;
          width: 16px;
          height: 16px;
          opacity: 0.5;
        }
      }
    }

    ${mediaQuery.medium(css`
      width: 50%;
    `)}

    ${mediaQuery.small(css`
      width: 100%;
    `)}
  `,
  Image: styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: ${Color.background};

    svg {
      color: ${Color.disabled};
    }
  `,
};

const EditContent = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  > button {
    margin-bottom: 0.5rem;
  }
`;

interface Props {
  company: WWWCompanyDocument;
  premium: boolean;
  loading: boolean;
  editable: boolean;
}

export const CompanyContactInfo: FC<Props> = ({ company, editable, premium, loading }) => {
  const { contactPersons, locations } = company;

  const dictionary = useCompanyDictionary();

  const isPremiumLoading = premium && loading;
  const isEmpty = !contactPersons.length && !locations.length;

  const renderLocation = (location: WWWCompanyDocument['locations'][number], index: number) => {
    const cityString = [location.postalCode, location.city, location.country].filter((x) => !!x).join(', ');
    const addressString = [location.street1, location.street2, cityString].filter((x) => !!x).join('\n');

    return (
      <Styled.Address key={index} style={{ opacity: location.isHidden ? 0.4 : 1 }}>
        <Styled.Image>
          <MapMarkerIcon size={38} />
        </Styled.Image>
        <div>
          <Typography variant="h5">{location.name}</Typography>
          {!!addressString && <Typography variant="body2">{addressString}</Typography>}
          {!!location.phone && (
            <Typography variant="body2">
              <PhoneIcon />
              {(!premium || isPremiumLoading) && <Skeleton width={100} loading={isPremiumLoading} />}
              {premium && <a href={`tel:${location.phone}`}>{location.phone}</a>}
            </Typography>
          )}
          {!!location.email && (
            <Typography variant="body2">
              <EmailIcon />
              {(!premium || isPremiumLoading) && <Skeleton width={100} loading={isPremiumLoading} />}
              {premium && <a href={`mailto:${location.email}`}>{location.email}</a>}
            </Typography>
          )}
        </div>
      </Styled.Address>
    );
  };

  const renderContactPerson = (person: WWWCompanyDocument['contactPersons'][number], index: number) => {
    const fullNameString = [person.firstname, person.lastname].filter((x) => !!x).join(' ');

    return (
      <Styled.Contact key={index}>
        <Styled.Image>
          <AccountIcon size={38} />
        </Styled.Image>
        <div>
          {!!fullNameString && <Typography variant="h5">{fullNameString}</Typography>}
          {!!person.title && <Typography variant="body2">{person.title}</Typography>}

          {!!person.location && (
            <Typography variant="body2">
              <MapMarkerIcon />
              {person.location}
            </Typography>
          )}
          {!!person.phone && (
            <Typography variant="body2">
              <PhoneIcon />
              {(!premium || isPremiumLoading) && <Skeleton width={100} loading={isPremiumLoading} />}
              {premium && <a href={`tel:${person.phone}`}>{person.phone}</a>}
            </Typography>
          )}
          {!!person.email && (
            <Typography variant="body2">
              <EmailIcon />
              {(!premium || isPremiumLoading) && <Skeleton width={100} loading={isPremiumLoading} />}
              {premium && <a href={`mailto:${person.email}`}>{person.email}</a>}
            </Typography>
          )}
        </div>
      </Styled.Contact>
    );
  };

  return (
    <Grid>
      <Column>
        {isEmpty && editable && (
          <Banner>
            <FormattedMessage
              id="company.contactInfo.editEmptyMessage"
              defaultMessage={'Yrityksellä ei ole vielä yhtään yhteyshenkilöä tai toimipaikkaa. Paina "Muokkaa" lisätäksesi ensimmäisen.'}
            />
          </Banner>
        )}

        <Item show={isPremiumLoading || locations.length}>
          <StyledLabel>
            {dictionary.locations} <PremiumIcon />
          </StyledLabel>
          <Styled.Row>{locations.filter((l) => !l.isHidden).map(renderLocation)}</Styled.Row>
        </Item>

        <Spacer show={isPremiumLoading || (locations.length && locations.length)} />

        <Item show={isPremiumLoading || contactPersons.length}>
          <StyledLabel>
            {dictionary.contactPersons} <PremiumIcon />
          </StyledLabel>
          <Styled.Row>{contactPersons.map(renderContactPerson)}</Styled.Row>
        </Item>
      </Column>
    </Grid>
  );
};

export const CompanyContactInfoEdit: CompanyEditDialogContent = ({ company, openSelect, closeSelect }) => {
  const intl = useIntl();

  const title = intl.formatMessage({ id: 'company.contactInfo.title', defaultMessage: 'Yhteystiedot' });

  const dictionary = useCompanyDictionary();

  return (
    <>
      <Header>{title}</Header>

      <EditContent>
        {/* <StyledLabel>
          <FormattedMessage id="company.contactInfo.automaticallyImported.title" defaultMessage="Automaattisesti luodut" />
        </StyledLabel>

        <Help>
          <FormattedMessage
            id="company.contactInfo.automaticallyImported.description"
            defaultMessage="Nämä toimipisteet ja toimihenkilöt päivittyvät automaattisesti eri tietolähteiden perusteella."
          />
        </Help> */}

        <StyledLabel>{dictionary.locations}</StyledLabel>
        <EditLocation parentTitle={title} onOpen={openSelect} onClose={closeSelect} />

        <StyledLabel>{dictionary.contactPersons}</StyledLabel>
        <EditContactPerson parentTitle={title} onOpen={openSelect} onClose={closeSelect} />
      </EditContent>
    </>
  );
};
